import { motion } from 'framer-motion';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_API_URL } from '../helpers/Config';
import toast from 'react-hot-toast';
import { TriggerBalanceUpdate } from '../helpers/TriggerBalanceUpdate';
import { FaArrowRight } from 'react-icons/fa';
import { MdSend } from 'react-icons/md';
import { getPageStyles, MOTION } from '../helpers/pageTheme';

const MIN_P2P_AMOUNT = 10; // Same as minimum withdrawal

const P2PTransfer = ({ theme = 'dark' }) => {
    const [userId, setUserId] = useState('');
    const [amount, setAmount] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [transferableUsdt, setTransferableUsdt] = useState(0); // usdt from API = transferable
    const [lockUsdt, setLockUsdt] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const navigate = useNavigate();
    const isDark = theme === 'dark';
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);
    const themeColors = {
        pageBg: styles.page.backgroundColor,
        pageText: isDark ? '#f9fafb' : '#111827',
        mutedText: styles.textMuted,
        cardBg: styles.card.backgroundColor,
        cardBorder: isDark ? '#1f2937' : '#e5e7eb',
        tertiaryBg: isDark ? '#0f172a' : '#f8fafc',
    };

    const FetchProfile = async () => {
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) {
                const usdt = parseFloat(data.data.usdt) || 0; // usdt = transferable
                const balance = data.data.balance;
                const lockUsdtVal = parseFloat(data.data.lock_usdt) || 0;
                setTransferableUsdt(usdt);
                setLockUsdt(lockUsdtVal);
                TriggerBalanceUpdate({ balance, usdt });
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            toast.error('Failed to load balance');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        FetchProfile();
    }, []);

    const formatNumber = (num) => {
        const n = parseFloat(num);
        if (isNaN(n)) return '0.00';
        return n.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const handleTransferClick = (e) => {
        e.preventDefault();
        setUserId((v) => v.trim());

        const targetUserId = parseInt(userId?.trim(), 10);
        const amountNum = parseFloat(amount);

        if (!userId?.trim()) {
            toast.error('Please enter the recipient User ID');
            return;
        }

        if (isNaN(targetUserId) || targetUserId <= 0) {
            toast.error('Please enter a valid User ID');
            return;
        }

        if (!amount || isNaN(amountNum) || amountNum <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        if (amountNum < MIN_P2P_AMOUNT) {
            toast.error(`Minimum transfer amount is $${MIN_P2P_AMOUNT} USDT`);
            return;
        }

        if (amountNum > transferableUsdt) {
            toast.error('Insufficient transferable USDT balance');
            return;
        }

        if (!password?.trim()) {
            toast.error('Please enter your password');
            return;
        }

        setShowConfirmModal(true);
    };

    const handleConfirmTransfer = async () => {
        const targetUserId = parseInt(userId?.trim(), 10);
        const amountNum = parseFloat(amount);

        setIsProcessing(true);
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/p2p-transfer`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
                user_id: targetUserId,
                amount: amountNum,
                password: password.trim(),
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.msg) {
                toast.success(data?.msg);
                setShowConfirmModal(false);
                setUserId('');
                setAmount('');
                setPassword('');
                FetchProfile();
            }
        } catch (error) {
            console.error('P2P transfer error:', error);
            toast.error(error?.response?.data?.error || 'Failed to process P2P transfer');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleMaxClick = () => {
        setAmount((parseFloat(transferableUsdt) || 0).toFixed(2));
    };

    if (isLoading) {
        return (
            <div
                className="container-fluid px-3 py-4"
                style={{
                    maxWidth: '480px',
                    margin: '0 auto',
                    minHeight: '100vh',
                    background: themeColors.pageBg,
                    color: themeColors.pageText,
                }}
            >
                <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted">Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div variants={MOTION.container} initial="hidden" animate="visible" className="d-flex flex-column gap-3">
            <style>{`
                .p2p-amount-input::-webkit-inner-spin-button,
                .p2p-amount-input::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .p2p-amount-input {
                    -moz-appearance: textfield;
                }
            `}</style>
            <motion.div variants={MOTION.item} style={styles.hero('linear-gradient(135deg, #0ea5e9 0%, #0284c7 55%, #0369a1 100%)')}>
                <div className="d-flex align-items-center gap-2 mb-1">
                    <MdSend size={26} className="text-white" />
                    <h4 className="mb-0 fw-bold text-white">P2P Transfer</h4>
                </div>
                <p className="mb-0 small text-white opacity-75">Send USDT to another user instantly</p>
            </motion.div>

            {/* USDT Balance Cards - Transferable & Locked only */}
            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="row g-2 mb-4"
                style={{ alignItems: 'stretch' }}
            >
                <div className="col-6 d-flex">
                    <div
                        className="card border-0 text-white w-100 d-flex flex-column"
                        style={{
                            background: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
                            borderRadius: '12px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            minHeight: '100px',
                        }}
                    >
                        <div className="card-body text-center p-3 d-flex flex-column justify-content-center">
                            <h6 className="card-title mb-1 opacity-75 mb-0" style={{ fontSize: '0.7rem' }}>
                                Transferable USDT
                            </h6>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '1.1rem' }}>
                                ${formatNumber(transferableUsdt)}
                            </h5>
                            <small className="opacity-75" style={{ fontSize: '0.65rem' }}>Available for P2P</small>
                        </div>
                    </div>
                </div>
                <div className="col-6 d-flex">
                    <div
                        className="card border-0 w-100 d-flex flex-column"
                        style={{
                            background: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.5)',
                            borderRadius: '12px',
                            border: `1px solid ${themeColors.cardBorder}`,
                            minHeight: '100px',
                        }}
                    >
                        <div className="card-body text-center p-3 d-flex flex-column justify-content-center">
                            <h6 className="card-title mb-1 opacity-75 mb-0" style={{ fontSize: '0.7rem', color: themeColors.mutedText }}>
                                Locked USDT
                            </h6>
                            <h5 className="mb-0 fw-bold" style={{ fontSize: '1.1rem', color: themeColors.pageText }}>
                                ${formatNumber(lockUsdt)}
                            </h5>
                            <small className="opacity-75" style={{ fontSize: '0.65rem', color: themeColors.mutedText }}>Locked funds</small>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Transfer Form */}
            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="card border-0 shadow-sm"
                style={{
                    background: themeColors.cardBg,
                    borderRadius: '12px',
                    border: `1px solid ${themeColors.cardBorder}`,
                }}
            >
                <div className="card-body p-3">
                    <form onSubmit={handleTransferClick}>
                        {/* User ID */}
                        <div className="mb-3">
                            <label
                                className="form-label small fw-semibold mb-2"
                                style={{ color: themeColors.mutedText }}
                            >
                                Recipient User ID
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="form-control"
                                placeholder="Enter user ID"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
                                style={{
                                    fontSize: '1rem',
                                    background: themeColors.tertiaryBg,
                                    color: themeColors.pageText,
                                    borderColor: themeColors.cardBorder,
                                    padding: '12px',
                                }}
                            />
                        </div>

                        {/* Amount */}
                        <div className="mb-3">
                            <label
                                className="form-label small fw-semibold mb-2"
                                style={{ color: themeColors.mutedText }}
                            >
                                Amount (USDT)
                            </label>
                            <div className="input-group">
                                <span
                                    className="input-group-text border-end-0 fw-bold"
                                    style={{
                                        background: themeColors.tertiaryBg,
                                        color: themeColors.mutedText,
                                        borderColor: themeColors.cardBorder,
                                    }}
                                >
                                    $
                                </span>
                                <input
                                    type="number"
                                    className="form-control border-start-0 fw-bold p2p-amount-input"
                                    placeholder="0.00"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                                    }}
                                    min={MIN_P2P_AMOUNT}
                                    max={transferableUsdt}
                                    style={{
                                        fontSize: '1.1rem',
                                        background: themeColors.tertiaryBg,
                                        color: themeColors.pageText,
                                        borderColor: themeColors.cardBorder,
                                    }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={handleMaxClick}
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    MAX
                                </button>
                            </div>
                            <div className="d-flex justify-content-between mt-1">
                                <small style={{ fontSize: '0.7rem', color: themeColors.mutedText }}>
                                    Available: ${formatNumber(transferableUsdt)}
                                </small>
                                <small style={{ fontSize: '0.7rem', color: themeColors.mutedText }}>
                                    Min: ${MIN_P2P_AMOUNT}
                                </small>
                            </div>
                        </div>

                        {/* Password */}
                        <div className="mb-4">
                            <label
                                className="form-label small fw-semibold mb-2"
                                style={{ color: themeColors.mutedText }}
                            >
                                Your Password
                            </label>
                            <div className="input-group">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-control"
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    style={{
                                        fontSize: '1rem',
                                        background: themeColors.tertiaryBg,
                                        color: themeColors.pageText,
                                        borderColor: themeColors.cardBorder,
                                        padding: '12px',
                                    }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ borderColor: themeColors.cardBorder }}
                                >
                                    {showPassword ? '🙈' : '👁️'}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <motion.button
                            type="submit"
                            className="btn btn-primary w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            disabled={
                                isProcessing ||
                                !userId?.trim() ||
                                !amount ||
                                parseFloat(amount) < MIN_P2P_AMOUNT ||
                                parseFloat(amount) > transferableUsdt ||
                                !password?.trim()
                            }
                            style={{
                                fontSize: '1rem',
                                background: styles.submitBtn.background,
                                boxShadow: styles.submitBtn.boxShadow,
                                border: 'none',
                                borderRadius: '12px',
                            }}
                        >
                            {isProcessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <FaArrowRight />
                                    Transfer USDT
                                </>
                            )}
                        </motion.button>
                    </form>
                </div>
            </motion.div>

            {/* Info Card */}
            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="card border-0 shadow-sm mt-3"
                style={{
                    background: themeColors.cardBg,
                    borderRadius: '12px',
                    border: `1px solid ${themeColors.cardBorder}`,
                }}
            >
                <div className="card-body p-3">
                    <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>
                        P2P Transfer Information
                    </h6>
                    <ul className="text-muted mb-0" style={{ fontSize: '0.75rem', lineHeight: '1.6' }}>
                        <li>Minimum transfer: ${MIN_P2P_AMOUNT} USDT</li>
                        <li>Only transferable USDT (usdt balance) can be sent</li>
                        <li>Transfers are instant and irreversible</li>
                        <li>Enter the correct User ID of the recipient</li>
                        <li>Your password is required to confirm the transfer</li>
                    </ul>
                </div>
            </motion.div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered"
                        style={{ maxWidth: '400px' }}
                    >
                        <div
                            className="modal-content"
                            style={{
                                background: themeColors.cardBg,
                                color: themeColors.pageText,
                                border: `1px solid ${themeColors.cardBorder}`,
                                borderRadius: '16px',
                            }}
                        >
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title fw-bold">Confirm P2P Transfer</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => !isProcessing && setShowConfirmModal(false)}
                                    disabled={isProcessing}
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body py-4">
                                {isProcessing ? (
                                    <div className="text-center">
                                        <div className="spinner-border text-primary mb-3" role="status">
                                            <span className="visually-hidden">Processing...</span>
                                        </div>
                                        <p className="mb-0" style={{ color: themeColors.mutedText }}>
                                            Processing your transfer...
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-center mb-4">
                                            <div
                                                className="rounded p-3 mb-2"
                                                style={{ backgroundColor: themeColors.tertiaryBg }}
                                            >
                                                <div
                                                    className="display-6 fw-bold"
                                                    style={{ color: '#6366f1' }}
                                                >
                                                    ${formatNumber(amount)} USDT
                                                </div>
                                                <small style={{ color: themeColors.mutedText }}>
                                                    Transfer Amount
                                                </small>
                                            </div>
                                        </div>

                                        <div className="border rounded p-3 mb-3" style={{ borderColor: themeColors.cardBorder }}>
                                            <div className="row mb-2" style={{ fontSize: '0.9rem' }}>
                                                <div className="col-6" style={{ color: themeColors.mutedText }}>
                                                    Recipient User ID:
                                                </div>
                                                <div className="col-6 text-end fw-semibold">
                                                    {userId?.trim()}
                                                </div>
                                            </div>
                                            <div className="row" style={{ fontSize: '0.9rem' }}>
                                                <div className="col-6" style={{ color: themeColors.mutedText }}>
                                                    Amount:
                                                </div>
                                                <div className="col-6 text-end fw-semibold">
                                                    ${formatNumber(amount)} USDT
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="alert alert-warning py-2 mb-0"
                                            style={{ fontSize: '0.8rem' }}
                                        >
                                            <strong>Please confirm:</strong> This action cannot be undone.
                                            Make sure the recipient User ID is correct before proceeding.
                                        </div>
                                    </>
                                )}
                            </div>
                            {!isProcessing && (
                                <div className="modal-footer border-0 pt-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowConfirmModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleConfirmTransfer}
                                    >
                                        Confirm Transfer
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
            </motion.div>
        </motion.div>
    );
};

export default P2PTransfer;
